<template>
  <div>
    <base-header class="pb-6" type="gray">
      <b-row align-v="center" class="py-4">
        <b-col lg="6" cols="7">
          <h6 class="h2 text-white d-inline-block mb-0">Account Overview</h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </b-col>
      </b-row>
      <!-- Card stats -->
      <b-row>
        <b-col md="6">
          <b-card>
            <template v-slot:header>
              <b-row v-if="!showAccountNameEdit">
                <b-col cols="10">
                  <h5 class="h3 mb-0">{{ account.name | decode }}</h5>
                </b-col>
                <b-col cols="2" class="text-right">
                  <base-button
                    size="sm"
                    type="default"
                    @click="showAccountNameEdit = true"
                  >
                    <i class="fa fa-pencil-alt mr-0"></i>
                    <span class="sr-only">Edit</span>
                  </base-button>
                </b-col>
              </b-row>
              <account-name-edit-form
                v-else
                :account="account"
                @update-account="accountUpdated"
                @cancel="showAccountNameEdit = false"
              ></account-name-edit-form>
            </template>

            <div class="text-center text-lg" v-if="subscriptionLoading">
              <i class="fa fa-spin fa-spinner mr-2"></i>Loading&hellip;
            </div>

            <b-card-body v-if="!subscriptionLoading && !subscription">
              <p>This account is currently using a free trial.</p>
              <p>
                The trial expiry date is
                {{ account.subscriptionExpiresAt | moment("MMMM Do YYYY") }}
              </p>
              <p>For full access, please subscribe to a paid plan:</p>
              <b-row>
                <b-col>
                  <h4 class="text-center">Solo Plan</h4>
                  <ul class="fa-ul">
                    <li>
                      <span class="fa-li"><i class="fas fa-check"></i></span>1
                      company
                    </li>
                    <li>
                      <span class="fa-li"><i class="fas fa-check"></i></span>1
                      user
                    </li>
                    <li>
                      <span class="fa-li"><i class="fas fa-check"></i></span
                      >Basic support
                    </li>
                  </ul>
                  <b-btn block @click="openCheckout(595817)" variant="info"
                    >Subscribe now</b-btn
                  >
                </b-col>
                <b-col>
                  <h4 class="text-center">Professional Plan</h4>
                  <ul class="fa-ul">
                    <li>
                      <span class="fa-li"><i class="fas fa-check"></i></span>10
                      companies
                    </li>
                    <li>
                      <span class="fa-li"><i class="fas fa-check"></i></span>5
                      users
                    </li>
                    <li>
                      <span class="fa-li"><i class="fas fa-check"></i></span
                      >Priority support
                    </li>
                  </ul>
                  <b-btn block @click="openCheckout(595823)" variant="primary"
                    >Subscribe now</b-btn
                  >
                </b-col>
              </b-row>

              <!-- TODO: make this a pretty button -->
            </b-card-body>

            <ul v-if="subscription">
              <li>
                <!-- plan name -->
                {{ subscription.plan.name }}
              </li>
              <li>
                Next payment date:
                {{ subscription.nextPayment.date | moment("MMMM Do YYYY") }}
              </li>
              <li v-if="account.subscriptionExpiresAt">
                Subscription expiry:
                {{ account.subscriptionExpiresAt | moment("MMMM Do YYYY") }}
              </li>
              <li>
                Account opened {{ account.createdAt | moment("from", "now") }}
              </li>
              <li>
                <a :href="subscription.updateUrl" target="_blank"
                  >Manage plan</a
                >
              </li>
              <li>
                <a :href="subscription.cancelUrl" target="_blank"
                  >Cancel subscription</a
                >
              </li>
            </ul>

            <b-list-group v-if="subscription" flush>
              <b-list-group-item
                v-for="payment in paidSubscriptionPayments"
                :key="payment.id"
                class="d-flex justify-content-between align-items-center"
              >
                <span>
                  Payment <code>{{ payment.id }}</code> for
                  <money-format
                    :value="payment.amount"
                    locale="en-GB"
                    :currency-code="payment.currency"
                    :subunits-value="false"
                    :hide-subunits="false"
                  >
                  </money-format>
                </span>

                <a
                  :href="payment.receiptUrl"
                  target="_blank"
                  v-b-tooltip.hover
                  title="Download invoice"
                  ><i class="fas fa-download"></i
                ></a>
              </b-list-group-item>
            </b-list-group>
          </b-card>
        </b-col>
        <b-col cols="6">
          <account-members :account-id="account.id"></account-members>
        </b-col>
      </b-row>
    </base-header>
    <b-container fluid class="mt--6">
      <b-row class="starter-page">Sample page</b-row>
    </b-container>
  </div>
</template>
<script>
import MoneyFormat from "vue-money-format";
import _ from "underscore";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";

import AccountMembers from "./AccountMembers";
import AccountNameEditForm from "./AccountNameEditForm";

export default {
  name: "account-page",
  components: {
    RouteBreadCrumb,
    AccountMembers,
    AccountNameEditForm,
    "money-format": MoneyFormat,
  },
  data() {
    return {
      showAccountNameEdit: false,
      subscription: null,
      subscriptionLoading: false,
    };
  },
  computed: {
    account: function () {
      return this.$store.state.account;
    },
    paidSubscriptionPayments: function () {
      if (!this.subscription) {
        return [];
      }
      return _.where(this.subscription.payments, { isPaid: 1 });
    },
  },
  beforeMount() {
    this.getAccountSubscription();
  },
  methods: {
    loadAccount: function () {
      this.axios
        .get(`v1/accounts/${this.account.id}`)
        .then((response) => {
          // store to account
          this.$store.commit("SET_ACCOUNT", response.data);
        })
        .catch(() => {});
    },
    getAccountSubscription: function () {
      this.subscriptionLoading = true;
      this.axios
        .get(`/v1/accounts/${this.account.id}/subscription`)
        .then((response) => {
          this.subscription = response.data;
          this.subscriptionLoading = false;
        })
        .catch((error) => {
          this.subscriptionLoading = false;

          console.error("error: ", error);
        });
    },
    accountUpdated: function (result) {
      this.showAccountNameEdit = false;
    },
    openCheckout: function (productId) {
      const instance = this;
      window.Paddle.Checkout.open({
        product: productId,
        email: instance.$auth.user().email.address,
        passthrough: JSON.stringify({ accountId: instance.account.id }),
        successCallback: function (data) {
          swal.fire({
            title: `Thank you!`,
            text: `Your subscription payment has been made successfully.`,
            buttonsStyling: false,
            confirmButtonClass: "btn btn-primary",
          });

          // wait for the webhook to save the subscription ID against the account
          const wait = 1000 * 5;
          setTimeout(() => {
            instance.loadAccount();
            instance.getAccountSubscription();
          }, wait);
        },
      });
    },
  },
};
</script>
<style>
.starter-page {
  min-height: calc(100vh - 380px);
}
</style>
